// ============================================================================
// Configuration Types
// ============================================================================

import { CharacteristicTypeOptionsConfig } from './ProductSheet_Characteristic';

/**
 * Configuration pour les champs à afficher dans la fiche produit
 * ET dans les variants (car le variant 0 = champs communs du produit)
 */
export interface ProductSheetFieldsConfig {
  // ============================================================================
  // Informations de base (niveau produit uniquement)
  // ============================================================================
  showCode?: boolean;           // Réf produit (uniquement niveau produit)
  showName?: boolean;           // Nom produit (uniquement niveau produit)
  showRequiredStatus?: boolean;
  showCatchword?: boolean;      // Slogan (uniquement niveau produit)
  showNameVariant0?: boolean;   // Nom du premier variant si variants existent
  showCategories?: boolean;     // Catégories (uniquement niveau produit)
  showPaymentRequired?: boolean; // Toggle paiement requis (uniquement niveau produit)
  showVariants?: boolean;       // Section variants (uniquement niveau produit)
  showAssociatedMedia?: boolean;
  showAssociatedUserStatus?: boolean;
  showAssociatedBroadcastingChannel?: boolean;
  showAssociatedAdminResource?: boolean;
  
  // ============================================================================
  // Champs communs (partagés entre produit/variant0 ET autres variants)
  // Ces champs sont configurés UNE SEULE FOIS et s'appliquent partout
  // ============================================================================
  showActive?: boolean;         // Toggle actif
  showBarcode?: boolean;        // Code barre
  showVariantCode?: boolean;    // Référence variant
  showVariantName?: boolean;    // Nom du variant
  showComplement?: boolean;     // Complément
  showDescription?: boolean;    // Description
  showPhotos?: boolean;         // Photos/médias
  showCharacteristics?: boolean; // Caractéristiques
  showPrices?: boolean;         // Prix
  
  // Champs de stock
  showStock?: boolean;          // Afficher la section stock
  showStockAvailable?: boolean;
  showStockThreshold?: boolean;
  showStockBooked?: boolean;    // Visible uniquement en mode édition
  showStockAssigned?: boolean;  // Visible uniquement en mode édition
}

/**
 * Configuration pour la validation des champs
 */
export interface ProductSheetValidationConfig {
  requireCode?: boolean;
  requireName?: boolean;
  requireBarcode?: boolean;
  requireCategories?: boolean;
  requireNameVariant0WhenVariantsExist?: boolean;
  
  // Validation personnalisée
  customValidation?: (fields: any) => { isValid: boolean; errors: Record<string, string> };
}

/**
 * Configuration complète pour un type de produit
 */
export interface ProductSheetConfig {
  // Type de produit (Physical Item, PayVideo, PayChannel, Subscription)
  productType: string;
  
  // Configuration des types de caractéristiques
  characteristicTypeOptionsConfig: CharacteristicTypeOptionsConfig;
  
  // Champs à afficher
  fieldsConfig: ProductSheetFieldsConfig;
  
  // Configuration de validation
  validationConfig: ProductSheetValidationConfig;
  
  // Labels personnalisés (optionnel)
  labels?: {
    title?: string;
    codeLabel?: string;
    nameLabel?: string;
    photosLabel?: string;
    characteristicsLabel?: string;
    // ... autres labels personnalisables
  };
}

// ============================================================================
// Configurations prédéfinies pour chaque type de produit
// ============================================================================

export const PRODUCT_SHEET_CONFIGS: Record<string, ProductSheetConfig> = {
  
  'Physical Item': {
    productType: 'Physical Item',
    
    characteristicTypeOptionsConfig: {
      Dimension: { units: ['m', 'cm', 'mm'], requiresName: true, requiresUnit: true },
      Poids:     { units: ['kg', 'g'],      requiresName: true, requiresUnit: true },
      Durée:     { units: ['h', 'm', 's'],  requiresName: true, requiresUnit: true },
      Couleur:   { units: [''],             requiresName: true, requiresUnit: false },
      URL:       { units: [''],             requiresName: false, requiresUnit: false },
    },
    
    fieldsConfig: {
      // Niveau produit uniquement
      showCode: true,
      showName: true,
      showRequiredStatus: true,
      showCatchword: true,
      showNameVariant0: true,
      showCategories: true,
      showPaymentRequired: true,
      showVariants: true,
      showAssociatedMedia: false,
	  showAssociatedUserStatus: false,
      showAssociatedBroadcastingChannel: false,
	  
      // Champs communs (variant 0 + autres variants)
      showActive: true,
      showBarcode: true,
      showVariantCode: true,
      showVariantName: true,
      showComplement: true,
      showDescription: true,
      showPhotos: true,
      showCharacteristics: true,
      showPrices: true,
      showStock: true,
      showStockAvailable: true,
      showStockThreshold: true,
      showStockBooked: true,
      showStockAssigned: true,
    },
    
    validationConfig: {
      requireCode: true,
      requireName: true,
      requireBarcode: false,
      requireCategories: true,
      requireNameVariant0WhenVariantsExist: true,
    },
    
    labels: {
      title: 'Fiche produit physique',
      characteristicsLabel: 'Dimension, poids, couleur, QRcode...',
    }
  },
  
  'PayVideo': {
    productType: 'PayVideo',
    
    characteristicTypeOptionsConfig: {
      URL: { units: [''], requiresName: false, requiresUnit: false },
    },
    
    fieldsConfig: {
      // Niveau produit uniquement
      showCode: true,
      showName: true,
      showRequiredStatus: true,
      showCatchword: false,
      showNameVariant0: false,
      showCategories: true,
      showPaymentRequired: true,
      showVariants: false,
      showAssociatedMedia: true,
	  showAssociatedUserStatus: false,
      showAssociatedBroadcastingChannel: false,
      
      // Champs communs (variant 0 seulement car pas de variants)
      showActive: true,
      showBarcode: false,
      showVariantCode: false,
      showVariantName: false,
      showComplement: false,
      showDescription: true,
      showPhotos: true,
      showCharacteristics: true,
      showPrices: true,
      showStock: false,
    },
    
    validationConfig: {
      requireCode: true,
      requireName: true,
      requireBarcode: false,
      requireCategories: true,
      requireNameVariant0WhenVariantsExist: false,
    },
    
    labels: {
      title: 'Fiche vidéo payante',
      characteristicsLabel: 'URL, informations complémentaires...',
    }
  },
  
  'PayChannel': {
    productType: 'PayChannel',
    
    characteristicTypeOptionsConfig: {
      Durée: { units: ['mois', 'années'], requiresName: false, requiresUnit: true },
      URL:   { units: [''],               requiresName: false, requiresUnit: false },
    },
    
    fieldsConfig: {
      // Niveau produit uniquement
      showCode: true,
      showName: true,
      showRequiredStatus: true,
      showCatchword: false,
      showNameVariant0: false,
      showCategories: true,
      showPaymentRequired: true,
      showVariants: true,
      showAssociatedMedia: false,
	  showAssociatedUserStatus: false,
      showAssociatedBroadcastingChannel: true,
      
      // Champs communs (variant 0 + autres variants)
      showActive: true,
      showBarcode: false,
      showVariantCode: true,
      showVariantName: true,
      showComplement: true,
      showDescription: true,
      showPhotos: true,
      showCharacteristics: true,
      showPrices: true,
      showStock: false,
    },
    
    validationConfig: {
      requireCode: true,
      requireName: true,
      requireBarcode: false,
      requireCategories: true,
      requireNameVariant0WhenVariantsExist: false,
    },
    
    labels: {
      title: 'Fiche chaîne payante',
      characteristicsLabel: 'Durée, URL, informations complémentaires...',
    }
  },
  
  'Subscription': {
    productType: 'Subscription',
    
    characteristicTypeOptionsConfig: {
      Durée: { units: ['mois', 'années'], requiresName: false, requiresUnit: true },
      URL:   { units: [''],               requiresName: false, requiresUnit: false },
    },
    
    fieldsConfig: {
      // Niveau produit uniquement
      showCode: true,
      showName: true,
      showRequiredStatus: false,
      showCatchword: false,
      showNameVariant0: true,
      showCategories: true,
      showPaymentRequired: true,
      showVariants: true,
      showAssociatedMedia: false,
	  showAssociatedUserStatus: true,
      showAssociatedBroadcastingChannel: false,
      
      // Champs communs (variant 0 + autres variants)
      showActive: true,
      showBarcode: false,
      showVariantCode: true,
      showVariantName: true,
      showComplement: true,
      showDescription: true,
      showPhotos: true,
      showCharacteristics: true,
      showPrices: true,
      showStock: false,
    },
    
    validationConfig: {
      requireCode: true,
      requireName: true,
      requireBarcode: false,
      requireCategories: true,
      requireNameVariant0WhenVariantsExist: true,
    },
    
    labels: {
      title: 'Fiche abonnement',
      characteristicsLabel: 'Durée, URL, informations complémentaires...',
    }
  },

  'CreatorCapacity': {
    productType: 'CreatorCapacity',

    characteristicTypeOptionsConfig: {
      // 'illimité' : palier spécial (durée de diffusion/enregistrement) lu par
      // SaleActionProcessor_Lovelight_TV_Buying_creator_capacity — il FIXE le champ sur
      // Admin::DUREE_SANS_LIMITE au lieu de l'additionner ; la valeur n'est pas utilisée.
      Quantité: { units: ['Go', 'unité', 'heure', 'illimité'], requiresName: false, requiresUnit: true },
    },

    fieldsConfig: {
      // Niveau produit uniquement
      showCode: true,
      showName: true,
      showRequiredStatus: false,
      showCatchword: false,
      showNameVariant0: true,
      showCategories: true,
      showPaymentRequired: true,
      showVariants: true,
      showAssociatedMedia: false,
      showAssociatedUserStatus: false,
      showAssociatedBroadcastingChannel: false,
      showAssociatedAdminResource: true,

      // Champs communs (variant 0 + autres variants)
      showActive: true,
      showBarcode: false,
      showVariantCode: true,
      showVariantName: true,
      showComplement: true,
      showDescription: true,
      showPhotos: false,
      showCharacteristics: true,
      showPrices: true,
      showStock: false,
    },

    validationConfig: {
      requireCode: true,
      requireName: true,
      requireBarcode: false,
      requireCategories: true,
      requireNameVariant0WhenVariantsExist: true,
    },

    labels: {
      title: 'Fiche quota créateur',
      characteristicsLabel: 'Quantité accordée à l\'achat...',
    }
  },

};

// ============================================================================
// Utilitaires
// ============================================================================

/**
 * Récupère la configuration pour un type de produit
 */
export function getProductSheetConfig(productType: string): ProductSheetConfig {
  const config = PRODUCT_SHEET_CONFIGS[productType];
  
  if (!config) {
    console.warn(`No configuration found for product type: ${productType}`);
    // Retourner une configuration par défaut
    return PRODUCT_SHEET_CONFIGS['Physical Item'];
  }
  
  return config;
}

/**
 * Crée une configuration personnalisée en fusionnant avec une configuration de base
 */
export function createCustomConfig(
  baseType: string,
  overrides: Partial<ProductSheetConfig>
): ProductSheetConfig {
  const baseConfig = getProductSheetConfig(baseType);
  
  return {
    ...baseConfig,
    ...overrides,
    fieldsConfig: {
      ...baseConfig.fieldsConfig,
      ...overrides.fieldsConfig,
    },
    validationConfig: {
      ...baseConfig.validationConfig,
      ...overrides.validationConfig,
    },
    characteristicTypeOptionsConfig: {
      ...baseConfig.characteristicTypeOptionsConfig,
      ...overrides.characteristicTypeOptionsConfig,
    },
    labels: {
      ...baseConfig.labels,
      ...overrides.labels,
    },
  };
}

/**
 * Valide les champs en fonction de la configuration
 */
export function validateFieldsWithConfig(
  fields: any,
  config: ProductSheetConfig,
  errorMessagesForVariants: any[],
  errorMessagesForMedias: any[],
  errorMessagesForCharacteristics: any[]
): { isValid: boolean; messages: Record<string, string> } {
  
  let isValid = true;
  const messages: Record<string, string> = {};
  
  const { validationConfig, fieldsConfig } = config;
  
  // Validation du code
  if (validationConfig.requireCode && fields.code === '') {
    messages.code = 'Il manque la référence produit';
    isValid = false;
  }
  
  // Validation du nom
  if (validationConfig.requireName && fields.name === '') {
    messages.name = 'Il manque le nom';
    isValid = false;
  }
  
  // Validation du nom du premier variant
  if (validationConfig.requireNameVariant0WhenVariantsExist && 
      fields.variantExistence && 
      fieldsConfig.showNameVariant0 &&
      fields.nameVariant0 === '') {
    messages.nameVariant0 = 'Il manque le nom du premier variant';
    isValid = false;
  }
  
  // Validation des catégories
  if (validationConfig.requireCategories && 
      fieldsConfig.showCategories &&
      fields.categoriesIds.length === 0) {
    messages.categoriesIds = 'Il faut choisir au moins une catégorie';
    isValid = false;
  }
  
  // Validation du code barre
  if (validationConfig.requireBarcode && 
      fieldsConfig.showBarcode &&
      fields.variants[0]?.barcode === '') {
    messages.barcode = 'Il manque le code barre';
    isValid = false;
  }
  
  // Validation personnalisée
  if (validationConfig.customValidation) {
    const customResult = validationConfig.customValidation(fields);
    if (!customResult.isValid) {
      isValid = false;
      Object.assign(messages, customResult.errors);
    }
  }
  
  return { isValid, messages };
}
